import { Localized, useLocalization } from "@fluent/react"
import { Alert, Box, Button, Card, CardHeader, Checkbox, DialogActions, DialogContent, DialogTitle, Divider, Grid, List, ListItem, ListItemButton, ListItemIcon, ListItemText, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material"
import React, { ChangeEvent, useEffect, useState } from "react";
import { CancelButton } from "../../../app/components/CancelButton";
import { showError, showSuccess } from "../../notifications/notificationsSlice";
import { useAppDispatch } from "../../../app/hooks";
import { CompanyMapping, createCompanyMappings, loadPotentialMappings, loadPotentialMappingsForCompany, PotentialMappingWithSections, PotentialSuppliers } from "./CompanyMappingsApi";
import { unwrapResult } from "@reduxjs/toolkit";
import produce from "immer"
import { If } from "../../../app/If";

interface FindPotentialCompanyMappingDialogProps {
    companyId?: string;
    onSaveSuccess: (companySupplierMappings: CompanyMapping[]) => void;
    onCancel: () => void;
}

interface PotentialSuppliersContainerProps {
    data: PotentialMappingWithSections;
    index: number;
    onSaveSuccess: (companySupplierMappings: CompanyMapping[]) => void;
}

const PotentialSuppliersContainer: React.FC<PotentialSuppliersContainerProps> = ({ data, index, onSaveSuccess }) => {
    const dispatch = useAppDispatch();
    const { l10n } = useLocalization();

    const [serverError, setServerError] = useState("");
    const [potentialMappings, setPotentialMappings] = useState<PotentialMappingWithSections>(data);
    const [filter, setFilter] = useState<"domain" | "email">("domain");
    const [filterEmail, setFilterEmail] = useState<string>("");
    const [selected, setSelected] = useState<string[]>([]);

    const handleToggle = (supplierId: string) => {
        setSelected(prev => prev.find(f => f === supplierId) ? prev.filter(f => f !== supplierId) : [...prev, supplierId]);
    }

    const handleToggleAll = (items: string[]) => {
        const notSelected = items.filter(supplierId => !selected.some(sel => sel === supplierId));

        setSelected(prev => [...prev, ...notSelected]);
    }

    const numberOfChecked = (items: string[]) => {
        return selected.filter(sel => items.includes(sel)).length;
    }

    const moveLeft = (itemsToMove: PotentialSuppliers[]) => {
        setPotentialMappings(produce((draft) => {
            if(draft) {
                draft.potentialSuppliers.push(...itemsToMove);
                draft.selectedPotentialSuppliers = draft.selectedPotentialSuppliers.filter(f => !itemsToMove.some(item => item.id === f.id))
            }
        }))
    }

    const moveRight = (itemsToMove: PotentialSuppliers[]) => {
        setPotentialMappings(produce((draft) => {
            if(draft) {
                draft.selectedPotentialSuppliers.push(...itemsToMove);
                draft.potentialSuppliers = draft.potentialSuppliers.filter(f => !itemsToMove.some(item => item.id === f.id))
            }
        }))
    }

    const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, filter: "domain" | "email") => {
        setFilter(filter);
    }

    const handleFilterEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFilterEmail(event.target.value);
    }

    const handleConfirm = async (suppliers: string[], companyId: string) => {
        if(suppliers.length === 0) return;
        
        try {
            const result = await dispatch(createCompanyMappings({ suppliers, companyId }))
            if (createCompanyMappings.fulfilled.match(result)) {
                onSaveSuccess(result.payload);
                dispatch(showSuccess("saved"))
            } else {
                let errorMsg = "error"
                if (result.payload && result.payload.kind === 'http') {
                    const problem = result.payload.problem
                    if (problem) {
                        errorMsg = problem.title

                    }
                }
                setServerError(errorMsg)
            }
        }
        catch (error) {
            dispatch(showError("error"));
        }
    }

    const renderList = (title: string, companyId: number, items: PotentialSuppliers[]) => (
        <Card sx={{ marginTop: 2, width: 420, height: 320, overflow: 'auto' }}>
            <CardHeader
                sx={{ px: 2, py: 1 }}
                avatar={
                    <Checkbox
                        onClick={() => handleToggleAll(items.map(m => m.id))}
                        checked={numberOfChecked(items.map(m => m.id)) === items.length && items.length !== 0}
                        indeterminate={
                            numberOfChecked(items.map(m => m.id)) !== items.length && numberOfChecked(items.map(m => m.id)) !== 0
                        }
                        disabled={items.length === 0}
                    />
                }
                title={title}
                subheader={`${l10n.getString("selected-count", null, "_Wybrano")} ${numberOfChecked(items.map(m => m.id))}/${items.length}`}
            />
            <Divider/>
            <List dense disablePadding>
                {items.map((item, index) => (
                    <ListItem key={item.id} disablePadding divider>
                        <ListItemButton onClick={() => handleToggle(item.id)}>
                            <ListItemIcon>
                                <Checkbox checked={!!selected.find(f => f === item.id)} />
                            </ListItemIcon>
                            <ListItemText
                                primary={`${index + 1}. ${item.supplierName}`}
                                secondary={
                                    <>
                                        {l10n.getString("company-supplier-mappings-supplier-email", null, "_E-mail dostawcy")}: {item.email}
                                        <br />
                                        {l10n.getString("company-supplier-mappings-contact-owner", null, "_Właściciel kontaktu")}: {item.companyName}
                                    </>
                                }
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Card>
    )

    return (
        <div key={index} style={{ marginBottom: '2rem' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5">{index + 1}. {potentialMappings.companyName} - {potentialMappings.companyEmail}</Typography>

                <Button variant="contained" color="primary" size="small" onClick={() => handleConfirm(potentialMappings.selectedPotentialSuppliers.map(m => m.id.toString()), potentialMappings.companyId.toString())}>
                    <Localized id="company-supplier-mappings-create">_Utwórz mapowanie</Localized>
                </Button>
            </Box>

            <ToggleButtonGroup value={filter} onChange={handleFilterChange} size="small" exclusive>
                <ToggleButton value="domain">
                    <Localized id="company-supplier-mappings-show-compatible-with-domain">_Pokaż zgodne z domeną</Localized>
                </ToggleButton>
                <ToggleButton value="email">
                    <Localized id="company-supplier-mappings-show-compatible-with-email">_Pokaż zgodne z adresem e-mail</Localized>
                </ToggleButton>
            </ToggleButtonGroup>

            <If condition={filter === "email"}>
                <TextField sx={{ marginLeft: 2 }} label={<Localized id="email">_Adres e-mail</Localized>} value={filterEmail} onChange={handleFilterEmailChange} size="small" />
            </If>

            <Typography sx={{ marginTop: 2 }}>
                <Localized id="company-supplier-mappings-private-suppliers">_Lista dopasowanych po mailu prywatnych dostawców</Localized>
            </Typography>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
                <Grid item>{renderList(l10n.getString("company-supplier-mappings-available-suppliers", null, "_Dostępni dostawcy"), potentialMappings.companyId, filter === "email" ? potentialMappings.potentialSuppliers.filter(f => f.email.includes(filterEmail)) : potentialMappings.potentialSuppliers)}</Grid>
                <Grid item>
                    <Grid container direction="column" alignItems="center" gap={1}>
                        <Button
                            variant="contained"
                            onClick={() => moveRight(potentialMappings.potentialSuppliers.filter(supplier => (selected.some(sel => sel === supplier.id))))}
                            disabled={selected.filter(id => potentialMappings.potentialSuppliers.some(i => i.id === id)).length === 0}
                        >
                            &gt;
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => moveLeft(potentialMappings.selectedPotentialSuppliers.filter(supplier => (selected.some(sel => sel === supplier.id))))}
                            disabled={selected.filter(id => potentialMappings.selectedPotentialSuppliers.some(i => i.id === id)).length === 0}
                        >
                            &lt;
                        </Button>
                    </Grid>
                </Grid>
                <Grid item>{renderList(l10n.getString("company-supplier-mappings-selected-suppliers", null, "_Wybrani dostawcy do utworzenia mapowania"), potentialMappings.companyId, potentialMappings.selectedPotentialSuppliers)}</Grid>
            </Grid>

            {
                serverError && <Alert sx={{ marginTop: 1, }} onClose={() => { setServerError("") }} severity="error">
                    <Localized id={serverError}>
                        <span>Server error</span>
                    </Localized>
                </Alert>
            }

            <Divider style={{ marginTop: '1.5rem' }} />
        </div>
    )
}

export const FindPotentialCompanyMappingDialog: React.FC<FindPotentialCompanyMappingDialogProps> = ({ companyId, onSaveSuccess, onCancel }) => {
    const dispatch = useAppDispatch();

    const [potentialMappings, setPotentialMappings] = useState<PotentialMappingWithSections[]>([]);
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        setSaving(true);
        if(!companyId) {
            dispatch(loadPotentialMappings())
                .then(unwrapResult)
                .then(result => {
                    setPotentialMappings(result.map(m => {
                        return { ...m, selectedPotentialSuppliers: [] }
                    }))
                })
                .catch((error) => console.error(error))
                .finally(() => setSaving(false));
        } else {
            dispatch(loadPotentialMappingsForCompany(companyId))
                .then(unwrapResult)
                .then(result => {
                    setPotentialMappings(result.map(m => {
                        return { ...m, selectedPotentialSuppliers: [] }
                    }))
                })
                .catch((error) => console.error(error))
                .finally(() => setSaving(false));
        }
    }, [dispatch]);

    return (
        <>
            <DialogTitle>
                <Localized id="company-supplier-mappings-potential-mappings">
                    <span>_Potencjalne mapowania: Zarejestrowana firma - prywatny dostawca</span>
                </Localized>
            </DialogTitle>
            <DialogContent>
                {potentialMappings?.map((group, groupIndex) => (
                    <PotentialSuppliersContainer data={group} index={groupIndex} onSaveSuccess={onSaveSuccess} /> 
                ))}
            </DialogContent>
            <DialogActions>
                <CancelButton loading={saving} onClick={onCancel} />
            </DialogActions>
        </>               
    )
}